

import { useEffect, useRef, useState } from 'react';
import { getAccessToken } from '@/services/auth/tokenManager';

export type RealtimeStatus = 'connecting' | 'open' | 'offline' | 'closed';

const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 15000;
const OFFLINE_AFTER = 3;

function buildPlainWsUrl(path: string): string | null {
  const token = getAccessToken();
  if (!token) return null;
  const base = process.env.Auth_URL || 'http://localhost:8080';
  const wsBase = /^https/i.test(base)
    ? base.replace(/^https/i, 'wss')
    : base.replace(/^http/i, 'ws');
  return `${wsBase}${path}?token=${encodeURIComponent(token)}`;
}

interface UsePlainRealtimeOpts {
  channel: 'llm';
  enabled: boolean;
  onPing?: () => void;
}

interface UsePlainRealtimeReturn {
  connected: boolean;
  status: RealtimeStatus;
}

export function usePlainRealtime(
  opts: UsePlainRealtimeOpts,
): UsePlainRealtimeReturn {
  const { channel, enabled } = opts;
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState<RealtimeStatus>('connecting');

  const cbRef = useRef(opts);
  useEffect(() => {
    cbRef.current = opts;
  }, [opts]);

  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    closedRef.current = false;
    const path = channel === 'llm' ? '/api/workspace/ws/llm' : '/api/workspace/ws/user';

    const clearReconnect = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const connect = () => {
      if (closedRef.current) return;
      const url = buildPlainWsUrl(path);
      if (!url) {

        scheduleReconnect();
        return;
      }
      if (retryRef.current < OFFLINE_AFTER) setStatus('connecting');
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
        setStatus('open');
      };
      ws.onmessage = () => {
        cbRef.current.onPing?.();
      };
      ws.onclose = () => {
        setConnected(false);
        if (wsRef.current === ws) wsRef.current = null;
        scheduleReconnect();
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {

        }
      };
    };

    const scheduleReconnect = () => {
      if (closedRef.current) return;
      clearReconnect();
      const delay = Math.min(
        RECONNECT_MAX,
        RECONNECT_BASE * 2 ** retryRef.current,
      );
      retryRef.current += 1;
      if (retryRef.current >= OFFLINE_AFTER) setStatus('offline');
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    connect();
    return () => {
      closedRef.current = true;
      clearReconnect();
      setConnected(false);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onclose = null;
        try {
          ws.close();
        } catch {

        }
      }
    };
  }, [channel, enabled]);

  return { connected, status };
}
